import axios from 'axios';
import { setHeader } from '.';

const BASE_URL = process.env.REACT_APP_BASE_API_URL;

/**
 * Fetches the subscription details for the current authenticated user
 * @returns a promise of the axios response
 */
export const fetchSubscriptionDetails = async () => {
  const request = await setHeader(axios);
  return request.get(`${BASE_URL}/subscription/details`);
};

/**
 * Checks the current status of the subscription. This is used to
 * efficently validate that the current user has a valid subscription
 * in various places.
 * @returns a promise of the axios response
 */
export const fetchSubscriptionStatus = async () => {
  const request = await setHeader(axios);
  return request.get(`${BASE_URL}/subscription/validate/`);
};

/**
 * Fetches the plans available to the current user to subscribe to
 * @returns a promise of the axios response
 */
export const fetchPlans = async () => {
  const request = await setHeader(axios);
  return request.get(`${BASE_URL}/subscription/plans/`);
};

/**
 * Fetches the currently active redeemed coupons on the account
 * of the current user
 * @returns a promise of the axios response
 */
export const fetchRedeemedCoupons = async () => {
  const request = await setHeader(axios);
  return request.get(`${BASE_URL}/subscription/coupons/`);
};

/**
 * Fetches the billing info from Recurly for the current logged in user
 * @returns a promise of the axios response
 */
export const fetchBillingDetails = async () => {
  const request = await setHeader(axios);
  return request.get(`${BASE_URL}/subscription/billing/details/`);
};

/**
 * Creates a new subscription for the current logged in user for the plan specified
 * @param {Object} subscriptionInfo - plan_code, billing_token, currency, referrer
 * @returns a promise of the axios response
 */
export const createSubscription = async (subscriptionInfo) => {
  const request = await setHeader(axios);
  return request.post(`${BASE_URL}/subscription/create/`, subscriptionInfo);
};

/**
 * Updates the billing info in Recurly for the current logged in user.
 * @param {String} billingToken -The billing token from recurly
 * @returns a promise of the axios response
 */
export const updateBillingInfo = async (billingToken) => {
  const request = await setHeader(axios);
  return request.put(`${BASE_URL}/subscription/billing/update/ `, billingToken);
};

/**
 * Updates the account profile info in Recurly for the current logged in user.
 * @param {Object} profileInfo - any of the following email, first_name, last_name
 * @returns a promise of the axios response
 */
export const updateProfile = async (profileInfo) => {
  const request = await setHeader(axios);
  return request.put(`${BASE_URL}/profile/update/ `, profileInfo);
};

/**
 * Cancels the active subscription for the user if they have one
 * @returns a promise of the axios response
 */
export const cancelSubscription = async () => {
  const request = await setHeader(axios);
  return request.put(`${BASE_URL}/subscription/cancel`);
};

/**
 * Deletes any pending change for the current user’s subscription if one exists
 * @returns axios response object
 */
export const cancelPendingChange = async () => {
  const request = await setHeader(axios);
  return request.delete(`${BASE_URL}/subscription/cancelpendingchange`);
};

/**
 * Deletes billing info on the current user’s account
 * @returns axios response object
 */
export const deleteBillingInfo = async () => {
  const request = await setHeader(axios);
  return request.delete(`${BASE_URL}/subscription/billing/delete`);
};

/**
 * Pauses the current user’s subscription renewal effective for the next billing cycle
 * @param {Number} PauseCycles - PauseCycles
 * @returns a promise of the axios response
 */
export const pauseSubscription = async (PauseCycles) => {
  const request = await setHeader(axios);
  return request.put(`${BASE_URL}/subscription/pause`, { PauseCycles });
};

/**
 * Redeems a coupon to the account of the logged in user if valid
 * @param {string} CouponCode - Coupon code to redeem
 * @returns a promise of the axios response
 */
export const redeemCoupon = async (CouponCode) => {
  const request = await setHeader(axios);
  return request.post(`${BASE_URL}/subscription/redeemcoupon`, CouponCode);
};

/**
 * Creates a plan change for the current user’s subscription that will take effect at the next billing date
 * @param {string} planCode
 * @returns a promise of the axios response
 */
export const updatePlan = async (planCode) => {
  const request = await setHeader(axios);
  return request.post(`${BASE_URL}/subscription/planchange`, {
    NewPlanCode: planCode,
  });
};

/**
 * Re-activates canceled subscription for user if they have one
 * Only on unexpired subscriptions
 * @returns a promise of the axios response
 */
export const reactivateSubscription = async () => {
  const request = await setHeader(axios);
  return request.put(`${BASE_URL}/subscription/reactivate/`);
};

/**
 * Immediately resumes the user’s current paused subscription
 * Bills the user for a full subscription period
 * Starts a new subscription cycle effective at the current date and time.
 * @returns a promise of the axios response
 */
export const resumeSubscription = async () => {
  const request = await setHeader(axios);
  return request.put(`${BASE_URL}/subscription/resume/`);
};

/**
 * Checks if the ID passed in belongs to an account that can currently
 * refer new accounts to LINKtivity learning.
 * @param {string} referrerId
 * @returns a promise of the axios response
 */
export const validateReferral = async (referrerId) => {
  return axios.get(`${BASE_URL}/subscription/validatereferral/${referrerId}`);
};

/**
 * Returns an object containing information about the account 
 * balance of the current user.
 * @returns a promise of the axios response
 */
export const fetchAccountBalance = async () => {
  const request = await setHeader(axios);
  return request.get(`${BASE_URL}/account/balance/`);
};